import React from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import type { EpkTrack, WebsiteContent, WebsiteEndorsement, WebsiteGalleryImage } from '../../data/website';
import { Button } from '../ui/Button';
import { Field, ImageField, TextField } from './EditorFields';
interface EpkEditorCardProps {
  draft: WebsiteContent;
  update: <Key extends keyof WebsiteContent>(key: Key, value: WebsiteContent[Key]) => void;
}
export function EpkEditorCard({
  draft,
  update
}: EpkEditorCardProps) {
  const updateQuote = (id: string, key: keyof WebsiteEndorsement, value: string) => update('epkQuotes', draft.epkQuotes.map(quote => quote.id === id ? {
    ...quote,
    [key]: value
  } : quote));
  const addQuote = () => update('epkQuotes', [...draft.epkQuotes, {
    id: `epk-quote-${Date.now()}`,
    artist: '',
    quote: '',
    imageUrl: ''
  }]);
  const removeQuote = (id: string) => update('epkQuotes', draft.epkQuotes.filter(quote => quote.id !== id));
  const updatePhoto = (id: string, key: keyof WebsiteGalleryImage, value: string) => update('epkPressPhotos', draft.epkPressPhotos.map(photo => photo.id === id ? {
    ...photo,
    [key]: value
  } : photo));
  const addPhoto = () => update('epkPressPhotos', [...draft.epkPressPhotos, {
    id: `epk-photo-${Date.now()}`,
    url: '',
    caption: ''
  }]);
  const removePhoto = (id: string) => update('epkPressPhotos', draft.epkPressPhotos.filter(photo => photo.id !== id));
  const updateTrack = (id: string, key: keyof EpkTrack, value: string) => update('epkTracks', draft.epkTracks.map(track => track.id === id ? {
    ...track,
    [key]: value
  } : track));
  const addTrack = () => update('epkTracks', [...draft.epkTracks, {
    id: `epk-track-${Date.now()}`,
    title: 'New song',
    duration: '',
    url: ''
  }]);
  const removeTrack = (id: string) => update('epkTracks', draft.epkTracks.filter(track => track.id !== id));
  return <div className="space-y-4" data-id="element-966" data-mp="26pe74wk3vr81">
      <ImageField label="Press kit hero photo" value={draft.epkHeroImageUrl} onChange={value => update('epkHeroImageUrl', value)} alt="Press kit hero photo" data-id="element-967" data-mp="26q5s7dg2u6k2" />
      <Field label="Hero photo description" value={draft.epkHeroImageAlt} onChange={value => update('epkHeroImageAlt', value)} helper="Describes the photo for screen readers and search engines." data-id="element-968" data-mp="19a9s0qlcqxkd" />
      <Field label="Eyebrow above the band name" value={draft.epkEyebrow} onChange={value => update('epkEyebrow', value)} data-id="element-969" data-mp="1i4o46kal5rz9" />
      <Field label="Notable stage" value={draft.epkFactDraw} onChange={value => update('epkFactDraw', value)} helper="Appears in the facts bar alongside genre, home base, and origin." data-id="element-970" data-mp="1s1ke8ug4um5x" />

      <Section title="Biography" data-id="element-971" data-mp="212ijth67z1qm">
        <Field label="Biography heading" value={draft.epkBioTitle} onChange={value => update('epkBioTitle', value)} data-id="element-972" data-mp="211pbibrm446d" />
        <TextField label="Biography" value={draft.epkBio} onChange={value => update('epkBio', value)} data-id="element-973" data-mp="2opcskp6ualp" />
        <p className="text-[11px] leading-relaxed text-zinc-500" data-id="element-974" data-mp="6q11mo08mrsu">Leave a blank line between paragraphs.</p>
      </Section>

      <Section title="Artist quotes" data-id="element-975" data-mp="3qjme4vjj46pb">
        {draft.epkQuotes.map(quote => <Row key={quote.id} label="Quote" onRemove={() => removeQuote(quote.id)} data-id="element-976" data-mp="3lk9jvpqbvxcp">
            <Field label="Artist" value={quote.artist} onChange={value => updateQuote(quote.id, 'artist', value)} data-id="element-977" data-mp="3li6sb19j1w5v" />
            <TextField label="Quote" value={quote.quote} onChange={value => updateQuote(quote.id, 'quote', value)} data-id="element-978" data-mp="i3kjyml93lw0" />
            <ImageField label="Artist photo" value={quote.imageUrl} onChange={value => updateQuote(quote.id, 'imageUrl', value)} alt={quote.artist} data-id="element-979" data-mp="d3yc6so6yxul" />
          </Row>)}
        <Button variant="secondary" size="sm" onClick={addQuote} data-id="element-980" data-mp="43i8pj7g7qpq">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-981" data-mp="2tu527ytydof6" /> Add quote
        </Button>
      </Section>

      <Section title="Press photos" data-id="element-982" data-mp="2otr53btw3yvx">
        {draft.epkPressPhotos.map((photo, index) => <Row key={photo.id} label={`Photo ${index + 1}`} onRemove={() => removePhoto(photo.id)} data-id="element-983" data-mp="gkspjs7b0k43" data-mpi={index}>
            <ImageField label="Photo" value={photo.url} onChange={value => updatePhoto(photo.id, 'url', value)} alt={photo.caption || `Press photo ${index + 1}`} data-id="element-984" data-mp="lcklyjd55k25" />
            <Field label="Caption" value={photo.caption} onChange={value => updatePhoto(photo.id, 'caption', value)} helper="Describe what the photo shows — press use this as the credit line." data-id="element-985" data-mp="bmr81bpj0suq" />
          </Row>)}
        <Button variant="secondary" size="sm" onClick={addPhoto} data-id="element-986" data-mp="qac9l16r6k0j">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-987" data-mp="v9p8ikawfhjh" /> Add press photo
        </Button>
      </Section>

      <Section title="Music" data-id="element-988" data-mp="3b1mh0ya7neog">
        {draft.epkTracks.map(track => <Row key={track.id} label="Song" onRemove={() => removeTrack(track.id)} data-id="element-989" data-mp="3b3piqfkq6w90">
            <div className="grid gap-2 sm:grid-cols-2" data-id="element-990" data-mp="3g1r271m2cxl0">
              <Field label="Song title" value={track.title} onChange={value => updateTrack(track.id, 'title', value)} data-id="element-991" data-mp="37inoxduy5stk" />
              <Field label="Length" value={track.duration} onChange={value => updateTrack(track.id, 'duration', value)} placeholder="3:15" data-id="element-992" data-mp="33h267bvqekbg" />
            </div>
            <Field label="Listen link" value={track.url} onChange={value => updateTrack(track.id, 'url', value)} helper="Leave blank for an unreleased song — it shows as Unreleased instead of a Play button." data-id="element-993" data-mp="3cirzkn96btbv" />
          </Row>)}
        <Button variant="secondary" size="sm" onClick={addTrack} data-id="element-994" data-mp="2yj92qqltc4ev">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-995" data-mp="2ykk5cn3iv64e" /> Add song
        </Button>
      </Section>

      <Section title="Booking contact" data-id="element-996" data-mp="2tpxr8c622zgm">
        <Field label="Contact label" value={draft.epkContactName} onChange={value => update('epkContactName', value)} data-id="element-997" data-mp="261wndgjto77j" />
        <div className="grid gap-2 sm:grid-cols-2" data-id="element-998" data-mp="212bqrcs4kaxz">
          <Field label="Booking email" value={draft.epkContactEmail} onChange={value => update('epkContactEmail', value)} data-id="element-999" data-mp="211in4kogq1k2" />
          <Field label="Phone (optional)" value={draft.epkContactPhone} onChange={value => update('epkContactPhone', value)} data-id="element-1000" data-mp="9yrvjzssvowz" />
        </div>
      </Section>

      <p className="rounded-xl border border-white/[0.07] bg-ink-800 p-3 text-[11.5px] leading-relaxed text-zinc-500" data-id="element-1001" data-mp="es4kkb1e5xq9">
        Live performance credits and the award shown on the press kit are pulled from the <strong className="font-medium text-zinc-300" data-id="element-1002" data-mp="3i8tf0ynp9gzk">About page</strong> section above, so they only need editing in one place.
      </p>
    </div>;
}
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="rounded-xl border border-white/[0.08] bg-ink-850 p-3" data-id="element-1003" data-mp="26s3iwfilv9">
      <h4 className="mb-2.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-zinc-400" data-id="element-1004" data-mp="16hg1bhlhobdk">{title}</h4>
      <div className="space-y-3" data-id="element-1005" data-mp="1bfrbjtfp4nwb">{children}</div>
    </div>;
}
function Row({
  label,
  children,
  onRemove
}: {
  label: string;
  children: React.ReactNode;
  onRemove: () => void;
}) {
  return <div className="rounded-xl border border-white/[0.08] bg-ink-800 p-3" data-id="element-1006" data-mp="1gleaekeojvv7">
      <div className="mb-2 flex items-center justify-between" data-id="element-1007" data-mp="1gnh2d9ze7z2k">
        <span className="text-[12px] font-medium text-zinc-300" data-id="element-1008" data-mp="1bsv0bxf7biv5">{label}</span>
        <button type="button" aria-label={`Remove ${label.toLowerCase()}`} onClick={onRemove} className="rounded-lg p-1 text-zinc-500 transition-colors duration-150 ease-out hover:bg-danger/10 hover:text-danger" data-id="element-1009" data-mp="17tm3ukss16vw">
          <Trash2Icon className="h-4 w-4" data-id="element-1010" data-mp="svk28g5b2vha" />
        </button>
      </div>
      <div className="space-y-2" data-id="element-1011" data-mp="svts83qcp2mz">{children}</div>
    </div>;
}